import { useRef, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { mapFieldErrors } from '@/api/fieldErrors';
import { useCurrentOperator } from '@/auth/authHooks';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { useActionError, type ActionError } from '@/components/useActionError';
import { SubmitButton } from '@/components/SubmitButton';
import { useFieldErrors } from '@/components/useFieldErrors';
import { useFreshOnOpen } from '@/components/useFreshOnOpen';
import { inviteEmailError, isSelfInvite, looksLikeEmail } from '@/tenant/inviteGuard';
import { useTenantApi } from '@/tenant/useTenant';
import { useToast } from '@/components/useToast';

export interface InviteDialogProps {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}

const FIELDS = ['email'] as const;

export function InviteDialog(props: InviteDialogProps) {
  // A fresh form on every open (TMMRSL-0071).
  return <InviteForm key={useFreshOnOpen(props.open)} {...props} />;
}

function InviteForm({ open, onClose, onDone }: InviteDialogProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  const api = useTenantApi();
  const describe = useActionError();
  const ownEmail = useCurrentOperator()?.email;
  const emailRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  const fields = useFieldErrors(FIELDS, { email: emailRef }, submitting);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!looksLikeEmail(email)) {
      fields.fail({ email: t('validation.email') });
      return;
    }
    if (isSelfInvite(email, ownEmail)) {
      fields.fail({ email: t('tenant.invite.self') });
      return;
    }
    setSubmitting(true);
    fields.reset();
    try {
      const result = await api.invite({ email: email.trim() });
      if (result.emailSent === false) {
        showToast(t('subscribers.toast.inviteEmailFailed', { email: email.trim() }), 'error');
      } else {
        showToast(t('subscribers.toast.invited', { email: email.trim() }));
      }
      onDone();
      onClose();
    } catch (err) {
      const action = describe(err);
      const emailError = inviteEmailError(err, t);
      if (emailError) {
        fields.fail({ email: emailError });
        return;
      }
      setError(action);
      fields.failMarked(mapFieldErrors(action.fieldKeys, { email: 'email' }), {
        email: t('subscribers.field.email'),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose}>
      <Box component="form" noValidate onSubmit={(e) => void handleSubmit(e)}>
        <DialogTitle>{t('subscribers.inviteTitle')}</DialogTitle>
        <DialogContent>
          <ActionErrorAlert error={error} sx={{ mb: 2 }} />
          <TextField
            required
            fullWidth
            type="email"
            margin="dense"
            label={t('subscribers.email')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              fields.clear('email');
            }}
            inputRef={emailRef}
            error={fields.errors.email !== undefined}
            helperText={fields.errors.email}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" loading={submitting}>
            {t('subscribers.invite')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
